import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    background-color: #f8fafc;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    padding: 2rem;
    box-sizing: border-box;
    overflow-y: auto;
`;

export const TopBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
`;

export const HeaderContent = styled.div`
    display: flex;
    flex-direction: column;
`;

export const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: color 0.2s;
    padding: 0;
    margin-bottom: 1.5rem;
    width: fit-content;

    &:hover {
        color: #1d86ff;
    }
`;

export const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: #ffffff;
    padding: 0.5rem 1.25rem 0.5rem 0.5rem;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    cursor: default;

    .user-details {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
    }

    .name {
        font-weight: 600;
        color: #0f172a;
        font-size: 0.95rem;
        line-height: 1.2;
    }

    .username {
        color: #64748b;
        font-size: 0.8rem;
        margin-top: 2px;
    }
`;

export const Header = styled.div`
    h1 {
        font-size: 2rem;
        color: #0f172a;
        margin-bottom: 0.5rem;
        font-weight: 700;
    }

    p {
        font-size: 1rem;
        color: #64748b;
        margin: 0;
    }
`;

export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #cbd5e1;
    font-size: 1.25rem;
    font-weight: 600;
    gap: 16px;
    
    svg {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        100% { transform: rotate(360deg); }
    }
`;

export const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    background: #fff;
    border-radius: 12px;
    border: 1px dashed #cbd5e1;
    color: #64748b;
    font-size: 1.1rem;
`;

export const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    animation: ${fadeIn} 0.5s ease-out;
`;

export const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    border: 1px solid #e2e8f0;
    display: flex;
    flex-direction: column;
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: #1d86ff;
    }

    .card-content {
        padding: 1.5rem;
        flex: 1;

        h3 {
            margin: 0 0 0.75rem 0;
            color: #0f172a;
            font-size: 1.25rem;
            font-weight: 600;
        }

        p {
            margin: 0;
            color: #475569;
            font-size: 0.95rem;
            line-height: 1.5;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
    }

    .card-action {
        padding: 1rem 1.5rem;
        background: #f8fafc;
        border-top: 1px solid #e2e8f0;
        color: #1d86ff;
        font-weight: 600;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: background 0.2s ease;
    }

    &:hover .card-action {
        background: #1d86ff;
        color: #fff;
    }
`;

export const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(11, 26, 48, 0.85);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 2rem;
    box-sizing: border-box;
    animation: fadeInOverlay 0.3s ease;

    @keyframes fadeInOverlay {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

export const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    height: 90vh;
    max-width: 800px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 24px;
    padding: 2.5rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
    position: relative;
    overflow: hidden;

    h2 {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
        color: #0f172a;
        text-align: center;
        font-size: 1.85rem;
        font-weight: 800;
        letter-spacing: -0.5px;
        border: none;
        padding: 0;
    }

    .description {
        margin: 0 0 1.5rem 0;
        font-size: 1.05rem;
        color: #475569;
        text-align: center;
        line-height: 1.6;
    }
`;

export const CloseButton = styled.button`
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    background: #f1f5f9;
    border: none;
    color: #64748b;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    z-index: 10;

    &:hover {
        background: #e2e8f0;
        color: #0f172a;
    }
`;

export const MediaContainer = styled.div`
    position: relative;
    margin-bottom: 1.8rem;
    width: 100%;
    height: 100%;
    background-color: #0f172a;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.25);
    transition: all 0.3s ease;

    > div, iframe {
        width: 100%;
        height: 100%;
    }
`;
